<template>
  <div class="flex items-center space-x-4">
    <div>
      <div class="rounded-full w-12 h-12 flex justify-center items-center relative z-20">
        <!-- Microphone Icon -->
        <div v-if="deviceType === DEVICE_NAME.MICROPHONE">
          <ViceButton
            :data-cy="`${deviceType}-toggle-${
              multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn
            }`"
            circle
            grey
            class="disabled:cursor-not-allowed w-12 h-12 transition-opacity hover:bg-gray-100"
            :class="{
              'bg-vice-teal-600 hover:bg-vice-teal-700':
                multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn,
            }"
            @click="toggleUserMicrophone"
            :disabled="
              multiCameraStore.externalAudioIO ||
              isDisabledMediaControls ||
              !multiCameraStore.devicesInitialized
            "
            v-tippy="{
              content: 'Toggle microphone',
              theme: 'light-border',
            }"
          >
            <MicrophoneOn
              v-if="multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn"
            />
            <MicrophoneMutedIcon v-else />
          </ViceButton>
        </div>
        <!-- Camera Icon -->
        <div v-else-if="deviceType === DEVICE_NAME.CAMERA" class="flex">
          <ViceButton
            :data-cy="`${deviceType}-toggle-${
              multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn
            }`"
            normal
            circle
            grey
            class="disabled:cursor-not-allowed w-12 h-12 transition-opacity hover:bg-gray-100"
            :class="{
              'bg-vice-teal-600 hover:bg-vice-teal-700':
                multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn,
            }"
            @click="toggleCamera"
            :disabled="cameraSelectDisabled"
            v-tippy="{
              content: 'Toggle main camera',
              theme: 'light-border',
            }"
          >
            <MainCameraIcon
              v-if="multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isCameraOn"
              class="h-14 w-14"
            />
            <MainCameraOff v-else />
            <ViceLoader colorClass="text-vice-teal-900" class="left-2" v-if="cameraLoading" />
          </ViceButton>

          <!-- Warning: Predefined camera not connected -->
          <DeviceNotConnectedBadge
            v-if="multiCameraStore.isPredefinedMainCameraDisconnected && sessionStore.isClinician"
            v-tippy="{
              content: 'Predefined camera is not connected',
              theme: 'light-border',
              placement: 'top',
              interactive: true,
              maxWidth: 250,
            }"
          />
        </div>
        <!-- Speaker Icon -->
        <div v-else-if="deviceType === DEVICE_NAME.SPEAKERS">
          <ViceButton circle grey class="w-12 h-12 bg-vice-teal-600 pointer-events-none">
            <img :src="deviceIcon" :alt="deviceType" width="20" height="20" class="" />
          </ViceButton>
        </div>
      </div>
    </div>

    <div>
      <Menu as="div" class="relative inline-block text-left">
        <div>
          <MenuButton
            @click="dropDownOpen = !dropDownOpen"
            :disabled="cameraSelectDisabled"
            class="
              items-center
              rounded-full
              text-sm
              font-medium
              text-white
              devices-dropdown
              grid
              justify-items-end
              w-20
              h-12
              -ml-16
              border-2 border-gray-800
            "
          >
            <ChevronUpIcon class="mr-1 h-6 w-6" v-if="dropDownOpen" aria-hidden="true" />
            <ChevronDownIcon class="mr-1 h-6 w-6" v-else aria-hidden="true" />
          </MenuButton>
        </div>

        <transition>
          <MenuItems
            class="
              absolute
              right-4
              bottom-7
              w-80
              origin-top-right
              rounded-md
              bg-white
              shadow-lg
              ring-1 ring-gray-300
              focus:outline-none
            "
            style="z-index: 20"
          >
            <div class="p-1">
              <MenuItem v-for="device in devices" :key="device.deviceId">
                <button
                  class="
                    p-2
                    flex
                    group
                    w-full
                    text-sm
                    rounded-md
                    justify-center
                    text-gray-900
                    hover:text-white
                    hover:bg-vice-teal-600
                  "
                  @click="chooseMediaSource(deviceType, device)"
                >
                  {{ device.label }}
                  <span
                    v-if="
                      [
                        multiCameraStore.selectedMicDeviceId,
                        multiCameraStore.selectedSpeakerDeviceId,
                        multiCameraStore.selectedCameraDeviceId,
                      ].includes(device.deviceId)
                    "
                    style="font-weight: bold; color: crimson"
                  >
                    &nbsp;(in use)
                  </span>
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </div>
  </div>
</template>
<script>
import { onClickOutside } from '@vueuse/core';
import { ref, watch, onMounted, computed } from 'vue';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/solid';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { directive } from 'vue-tippy';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import {
  CAMERA_TYPES,
  DEVICE_NAME,
  SMART_GLASS_DEVICE_LABEL,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import MicrophoneMutedIcon from '@/Components/Icons/MicrophoneMutedIcon.vue';
import MicrophoneOn from '@/Components/Icons/MicrophoneOn.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import useSessionStore from '@/Stores/useSessionStore';
import MainCameraIcon from '@/Components/Icons/MainCameraIcon.vue';
import MainCameraOff from '@/Components/Icons/MainCameraOff.vue';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import DeviceNotConnectedBadge from '@/Components/Badges/DeviceNotConnectedBadge.vue';

export default {
  name: 'DefaultDeviceSelectorTile',
  computed: {
    VICE_SESSION_PHASES() {
      return VICE_SESSION_PHASES;
    },
    DEVICE_NAME() {
      return DEVICE_NAME;
    },
    CAMERA_TYPES() {
      return CAMERA_TYPES;
    },
  },
  directives: {
    tippy: directive,
  },
  props: {
    deviceIcon: {
      type: String,
      required: true,
    },
    deviceType: {
      type: String,
      required: true,
    },
    deviceKind: {
      type: String,
      required: true,
    },
    mediaType: {
      type: String,
      required: true,
    },
    defaultDevices: {
      type: Object,
      required: true,
    },
  },
  components: {
    DeviceNotConnectedBadge,
    ChevronUpIcon,
    ViceLoader,
    MainCameraOff,
    MainCameraIcon,
    MicrophoneOn,
    MicrophoneMutedIcon,
    ChevronDownIcon,
    Menu,
    MenuItem,
    MenuItems,
    MenuButton,
    ViceButton,
  },
  setup(props) {
    const dropDownOpen = ref(false);
    const cameraLoading = ref(false);
    const sessionStore = useSessionStore();
    const multiCameraStore = useLocalStreamingDevicesStore();
    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    const internalCameraType = CAMERA_TYPES.INTERNAL_WEBCAM;

    const isDisabledMediaControls = computed(
      () =>
        !multiCameraStore.mediaActivated ||
        sessionStore.isPutOnHold ||
        (sessionStore.isPhasePaused && sessionStore.isClinician) ||
        // !props.jitsiRoom ||
        sessionStore.mediaPermissionError,
    );

    const cameraSelectDisabled = computed(() =>
      Boolean(
        (sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION &&
          props.deviceType === DEVICE_NAME.CAMERA &&
          !sessionStore.isClinician) ||
          isDisabledMediaControls.value ||
          cameraLoading.value,
      ),
    );

    const devices = ref([]);

    onMounted(() => {
      devices.value =
        multiCameraStore.groupedDeviceAsAudioAndVideo[props.mediaType]?.[props.deviceType];
    });

    // watching for Pinia storage initialization to get the list of devices according to
    // device kind
    watch(
      () => deviceAndSessionSetupStore.getConnectedDevices(props.deviceKind),
      (devicesList) => {
        devices.value = devicesList;
      },
    );

    const isActiveDevice = (deviceName) =>
      multiCameraStore.allCameraTypes[internalCameraType].deviceSelection[deviceName].active;

    const chooseMediaSource = async (deviceName, prop) => {
      multiCameraStore.markSessionSetupAsChanged();
      await multiCameraStore.chooseMediaSource(internalCameraType, deviceName, prop);
      await toggleCamera();
    };

    const audioDeviceDisplay = (deviceProps, deviceName) =>
      deviceProps[0]?.kind.startsWith('audio') && multiCameraStore.externalAudioIO
        ? SMART_GLASS_DEVICE_LABEL
        : multiCameraStore.activeDeviceLabels[deviceName];

    // Allow audio selection for just the internal camera so that we have only one source
    const isDisabledAudioSelection = (mediaType) => mediaType === 'AUDIO';

    const toggleUserMicrophone = () => {
      multiCameraStore.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const toggleCamera = async () => {
      if (sessionStore.isClinician) {
        sessionStore.toggleControlPanel(true);
      }

      cameraLoading.value = true;
      await multiCameraStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM);
      cameraLoading.value = false;
    };

    onClickOutside(dropDownOpen, () => {
      dropDownOpen.value = false;
    });

    return {
      devices,
      sessionStore,
      dropDownOpen,
      cameraLoading,
      multiCameraStore,
      cameraSelectDisabled,
      isDisabledMediaControls,
      toggleCamera,
      isActiveDevice,
      chooseMediaSource,
      audioDeviceDisplay,
      toggleUserMicrophone,
      isDisabledAudioSelection,
    };
  },
};
</script>
<style scoped>
.device-type-label-regular {
  color: #881337;
  font-size: 16px;
}

.device-label-regular {
  font-size: 12px;
  color: #4b4b4b;
}
.devices-dropdown {
  background-color: #1e1e1e;
  color: #d4d4d4;
}
</style>
